import { WorkingHoursType } from '../../features/working-hours/types';
import { LangType } from '../../helpers/lang';
import { GridTemplateType } from '../../helpers/mappers';
import { buildParams } from '../../helpers/path';
import { PaginationRequest } from '../../types/pagination';
import {
  CustomCacheRequest,
  DefaultRequest,
  SortRequest,
  SortType,
  WithArchiveRequest,
} from '../../types/props';
import { DishType } from './dish-api';
import { DishCategoryType } from './dish-category-api';
import { DishSectionType } from './dish-section-api';
import { ImageType } from './image-api';
import { managerApiUrl } from './manager-api';
import { baseFetch } from './utils.ts/base-fetch';
import { downloadFile, fileFetch } from './utils.ts/file-fetch';

const baseUrl = `${managerApiUrl}/menu`;

export type DishMenuType = {
  id: string;
  name: LangType;
  kaspi_link?: string;
  description: LangType;
  is_on: boolean;
  qr_ids: string[];
  place_id?: number;
  tag_ids: number[];
  dishes?: DishType[];
  images?: { main: ImageType };
  dishes_count: number;
  working_hours?: WorkingHoursType;
  sections?: DishSectionType[];
  section_ids?: number[];
  categories?: DishCategoryType[];
  accept_minutes?: number;
  sort_order?: number;
  paysage?: Partial<{
    api_key: string;
    merchant_id: string;
    merchant_name: string;
    service_id: string;
    group_time?: number;
  }>;
};

export const fetchMenu = async (id: string): Promise<DishMenuType> => {
  const response = await baseFetch(baseUrl + `/menu/` + id);
  return response;
};

export type FetchMenuListRequest = {
  name?: string;
  is_on?: string;
  menuName?: string;
  counts?: string;
  tags?: string;
  description?: string;
  qrs?: string;
  dishesCount?: string;
  field?: 'name' | string;
  dir?: SortType | string;
} & PaginationRequest &
  CustomCacheRequest &
  SortRequest &
  DefaultRequest &
  WithArchiveRequest;

export type MenuUploadRequest = FormData;

export type MenuUploadType = {
  preview: {
    section: LangType;
    category: LangType;
    dish: Omit<DishType, 'prices'> & {
      price: number;
    };
  }[];
  upload_token: string;
};

export const fetchMenuList = async (
  params: FetchMenuListRequest,
): Promise<{ menus: DishMenuType[] }> => {
  const response = await baseFetch(baseUrl + `/menu` + buildParams(params));
  return response;
};

export const postMenu = async (data: Partial<DishMenuType>): Promise<DishMenuType> => {
  const response = await baseFetch(baseUrl + `/menu`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response;
};

export const putMenu = async (data: Partial<DishMenuType>): Promise<DishMenuType> => {
  const response = await baseFetch(baseUrl + '/menu/' + data.id, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  return response;
};

export const deleteMenu = async (id: string): Promise<void> => {
  const response = await baseFetch(baseUrl + '/menu/' + id + '/archive', {
    method: 'PUT',
  });
  return response;
};

export const cloneMenu = async (data: Partial<DishMenuType>): Promise<DishMenuType> => {
  const response = await baseFetch(baseUrl + `/menu/` + data.id + '/clone', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response;
};

export const putMenuOrder = async (order: string[]) => {
  const response = await baseFetch(baseUrl + `/menu/order`, {
    method: 'PUT',
    body: JSON.stringify({ order }),
  });
  return response;
};

export const menuUpload = async (data: MenuUploadRequest): Promise<MenuUploadType> => {
  const response = await baseFetch(baseUrl + '/menu/upload', {
    method: 'POST',
    body: data,
  });
  return response;
};

export const menuUploadConfirm = async (upload_token: string) => {
  await baseFetch(baseUrl + '/menu/confirm-upload', {
    method: 'POST',
    body: JSON.stringify({ upload_token }),
  });
};

export const menuTemplateDownload = async (type: GridTemplateType) => {
  const resp = await fileFetch(baseUrl + '/menu/template' + buildParams({ type }));
  downloadFile(resp.blob, resp.fileName || 'menu template.' + type);
};
